// src/components/core/migration/ImportDocumentModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect } from 'react';
import { Alert, Collapse, Flex, Typography, Switch, Tooltip, theme } from 'antd';
import { ApiOutlined, QuestionCircleOutlined, FileSyncOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import useZustand from 'hooks/useZustand';

const { Text } = Typography;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const ImportOrderStepContent = ({
  enrichCnpj,
  setEnrichCnpj,
  enrichCpf,
  setEnrichCpf,
  creditsBalance,
  isLoadingCredits,
  enrichmentPrices,
  cpfEnabled = false,
}) => {
  const { goFocus } = useSimpleFormContext();
  const { token: antToken } = theme.useToken();
  const setSettingsDrawerOpen = useZustand('theme', 'setSettingsDrawerOpen', false);

  // Calculate credits for each enrichment type (1 credit = R$ 0.10)
  const cnpjCredits = Math.ceil((enrichmentPrices?.recipientByCnpj || 0) * 10);
  const cpfCredits = Math.ceil((enrichmentPrices?.recipientByCpf || 0) * 10);

  // Calculate required credits based on selected enrichments
  const requiredCredits = (enrichCnpj ? cnpjCredits : 0) + (enrichCpf ? cpfCredits : 0);
  const hasEnoughCredits = creditsBalance >= requiredCredits || requiredCredits === 0;
  const hasAnyEnrichmentEnabled = enrichCnpj || enrichCpf;

  const willBeEnrich = enrichCnpj || enrichCpf;

  // Auto-focus the input field when step mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      goFocus?.('legacyCode', true);
    }, 0);
    return () => clearTimeout(timer);
  }, [goFocus]);

  // Handler to open credits drawer
  const handleOpenCreditsDrawer = () => {
    setSettingsDrawerOpen(true);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // STYLES
  // ─────────────────────────────────────────────────────────────────────────────
  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: `1px solid ${antToken.colorBorderSecondary}`,
  };

  const lastRowStyle = {
    ...rowStyle,
    borderBottom: 'none',
  };

  const titleContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical style={{ width: '100%' }} gap="middle" className="mt-3">
      <SimpleInput
        name="legacyCode"
        label="document_reference"
        styleItem={{ marginBottom: 0 }}
        required
        maxLength={14}
        style={{ width: '100%' }}
      />

      {/* Enrichment Collapse */}
      <Collapse
        ghost
        styles={{ header: { padding: '16px 0 0' } }}
        items={[
          {
            key: 'enrichment',
            label: (
              <Flex align="center" gap={8}>
                <FileSyncOutlined style={{ fontSize: 16 }} />
                <span>{translateX('data_enrichment')}</span>
              </Flex>
            ),
            children: (
              <Flex vertical>
                {/* CNPJ Row */}
                <div style={rowStyle}>
                  <div style={titleContainerStyle}>
                    <Text>{translateX('cnpj')}</Text>
                    <Tooltip title={translateX('enrichment_by_api')}>
                      <ApiOutlined style={{ color: antToken.colorText, fontSize: 14 }} />
                    </Tooltip>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      (~{cnpjCredits} {translateX('credits')})
                    </Text>
                    <Tooltip title={translateX('enrich_cnpj_charge_tooltip')}>
                      <QuestionCircleOutlined
                        style={{ color: '#8c8c8c', cursor: 'normal', fontSize: 12 }}
                      />
                    </Tooltip>
                  </div>
                  <Switch
                    size="small"
                    checked={enrichCnpj}
                    onChange={setEnrichCnpj}
                    disabled={isLoadingCredits}
                  />
                </div>

                {/* CPF Row */}
                <div style={lastRowStyle}>
                  <div style={titleContainerStyle}>
                    <Text>{translateX('cpf')}</Text>
                    <Tooltip title={translateX('enrichment_by_api')}>
                      <ApiOutlined style={{ color: antToken.colorText, fontSize: 14 }} />
                    </Tooltip>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      (~{cpfCredits} {translateX('credits')})
                    </Text>
                    <Tooltip title={translateX('enrich_cpf_charge_tooltip')}>
                      <QuestionCircleOutlined
                        style={{ color: '#8c8c8c', cursor: 'normal', fontSize: 12 }}
                      />
                    </Tooltip>
                  </div>
                  <Switch
                    size="small"
                    checked={enrichCpf}
                    onChange={setEnrichCpf}
                    disabled={isLoadingCredits || !cpfEnabled}
                  />
                </div>
              </Flex>
            ),
          },
        ]}
      />

      {/* Alert based on enrichment selection and credits */}
      {willBeEnrich && !hasEnoughCredits ? (
        <Alert
          type="warning"
          showIcon
          title={
            <span>
              {translateX('your_balance_is_plain')}{' '}
              <strong>{isLoadingCredits ? '...' : creditsBalance}</strong> {translateX('credits')}.{' '}
              <a onClick={handleOpenCreditsDrawer} style={{ cursor: 'pointer' }}>
                {translateX('click_to_acquire_credits')}
              </a>
            </span>
          }
        />
      ) : !willBeEnrich ? (
        <Alert type="warning" showIcon title={translateX('migration_without_data_enrichment')} />
      ) : null}
    </Flex>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const MigrationDocumentModalStep1 = ({
  enrichCnpj,
  setEnrichCnpj,
  enrichCpf,
  setEnrichCpf,
  creditsBalance,
  isLoadingCredits,
  enrichmentPrices,
}) => ({
  stepTitle: 'import_order_invoice',
  stepTooltip: 'import_order_tooltip',
  content: () => (
    <ImportOrderStepContent
      enrichCnpj={enrichCnpj}
      setEnrichCnpj={setEnrichCnpj}
      enrichCpf={enrichCpf}
      setEnrichCpf={setEnrichCpf}
      creditsBalance={creditsBalance}
      isLoadingCredits={isLoadingCredits}
      enrichmentPrices={enrichmentPrices}
    />
  ),
  validationFields: ['legacyCode'],
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default MigrationDocumentModalStep1;
